/**
 * Dates for the UI only.
 *
 * `Intl` is deliberately avoided in src/shared/roi/format.ts because the engine's
 * output is golden-file tested and must not vary with the host's ICU build. None
 * of that applies here: this is chrome, it never crosses the wire, and a person
 * reading "12 Mar 2026" wants their own locale's idea of it.
 */

const DATE = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : DATE.format(date);
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : DATE_TIME.format(date);
}

const CLOCK = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

/** "12:04" — the time alone, for a save that happened while you were watching. */
export function formatClock(date: Date): string {
  return Number.isNaN(date.getTime()) ? "—" : CLOCK.format(date);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "4 minutes ago", for the "last activity" line on a card. Past two weeks it
 * gives the date instead: "23 days ago" is arithmetic the reader has to undo.
 */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const elapsed = now - date.getTime();
  if (elapsed < 0) return formatDate(iso);
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return plural(Math.floor(elapsed / MINUTE), "minute");
  if (elapsed < DAY) return plural(Math.floor(elapsed / HOUR), "hour");
  if (elapsed < 14 * DAY) return plural(Math.floor(elapsed / DAY), "day");
  return formatDate(iso);
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? "" : "s"} ago`;
}
